import { Button, Progress, useToast } from '@quadminds/mindkit';
import { RiCheckboxCircleFill, RiCloseCircleFill } from '@remixicon/react';
import { AxiosProgressEvent } from 'axios';
import { FileDown, FileX, X } from 'lucide-react';
import { FC, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import YpfIntegrationModalBase from './YpfIntegrationModalBase';
import YpfIntegrationModalSection from './YpfIntegrationModalSection';
import { useGetYpfTemplate } from '../../../../hooks/useGetYpfTemplate';
import { useUploadYpfFile } from '../../../../hooks/useUploadYpfFile';
import { getFileSize } from '../../../../utils/utils';
import { YpfImportResponse } from '../Connections/Connections';

interface YpfIntegrationUploadProps {
    isOpen: boolean;
    handleCloseModal: (value: boolean) => void;
    onSuccess: (response: YpfImportResponse, file: File) => void;
}

const YpfIntegrationUpload: FC<YpfIntegrationUploadProps> = ({ isOpen, handleCloseModal, onSuccess }) => {
    const { t } = useTranslation();
    const { toast } = useToast();
    const { mutateAsync: uploadYpfFile, isPending } = useUploadYpfFile();
    const { mutateAsync: downloadTemplate, isPending: isLoadingTemplate } = useGetYpfTemplate();

    const [isDraggingOver, setIsDraggingOver] = useState<boolean>(false);
    const [fileSelected, setFile] = useState<File | null>(null);
    const [progress, setProgress] = useState<number>(0);
    const [result, setResult] = useState<'success' | 'error' | 'idle'>('idle'); // Puedes definir un tipo más específico si lo deseas

    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleDragAndDrop = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();

        if (progress > 0) {
            setProgress(0);
        }
        if (result !== 'idle') {
            setResult('idle');
        }

        if (event.dataTransfer.files) {
            // Aquí puedes manejar los archivos subidos
            const file = event.dataTransfer.files[0];
            if (file) {
                setFile(file);
            }
        }
        setIsDraggingOver(false);
    };
    const handleFileSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (progress > 0) {
            setProgress(0);
        }
        if (result !== 'idle') {
            setResult('idle');
        }
        if (event.target.files) {
            const file = event.target.files[0];
            if (file) {
                setFile(file);
            }
        }
    };

    const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        if (!isDraggingOver) setIsDraggingOver(true);
    };

    const handleDragExit = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        if (isDraggingOver) setIsDraggingOver(false);
    };

    const openFileSelector = () => {
        fileInputRef.current?.click();
    };
    const handleProgressEvent = (event: AxiosProgressEvent & { name: string }) => {
        const percentage = Math.floor((event.progress || 0) * 100);
        setProgress(percentage);
    };

    const resetStates = () => {
        setFile(null);
        setProgress(0);
        setResult('idle');
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };
    const handleUpload = useCallback(() => {
        if (!fileSelected) return;
        uploadYpfFile({ file: fileSelected, handleProgressEvent })
            .then((result) => {
                setResult('success');
                setTimeout(() => {
                    resetStates();
                    // Transform the result to match the expected YpfImportResponse type
                    onSuccess(
                        {
                            ok: true,
                            data: result,
                        },
                        fileSelected,
                    );
                }, 1500);
            })
            .catch((err) => {
                setResult('error');
                setTimeout(() => {
                    resetStates();
                    onSuccess({ ok: false, error: { details: { code: err.errCode } } }, fileSelected);
                }, 1500);
            });
    }, [fileSelected, onSuccess, uploadYpfFile]);

    const handleClose = () => {
        handleCloseModal(false);
        resetStates();
    };

    const handleDownloadTemplate = () => {
        downloadTemplate()
            .then((res) => {
                const url = window.URL.createObjectURL(res as unknown as Blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = 'archivo.xlsx';
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
            })
            .catch(() =>
                toast({
                    title: t('settings.connections.error.downloadFileTitleError'),
                    description: t('settings.connections.error.downloadFileDescriptionError'),
                    variant: 'destructive',
                }),
            );
    };

    return (
        <YpfIntegrationModalBase isOpen={isOpen} handleCloseModal={handleClose} title="Subir Archivo">
            <YpfIntegrationModalSection>
                <p className="font-roboto text-white font-normal text-[14px] leading-4 mb-3">
                    {t('settings.connections.templateMessage')}
                </p>
                <Button
                    variant="outline"
                    className="w-48"
                    onClick={handleDownloadTemplate}
                    disabled={isLoadingTemplate}
                >
                    <FileDown className="h-4 w-4 mr-2" />
                    <p>{t('settings.connections.templateButton')}</p>
                </Button>
            </YpfIntegrationModalSection>
            <YpfIntegrationModalSection>
                <div
                    className={twMerge(
                        'w-full flex flex-col gap-2 justify-center rounded-md items-center border border-dashed bg-[#5417EE0A] dark:bg-[#37474F] border-[#B998FA] dark:border-[#78909C] p-6',
                        isDraggingOver &&
                            'border-2 bg-primary/10 dark:bg-primary/20 border-primary/10 dark:border-primary/20',
                    )}
                    onDrop={handleDragAndDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragExit}
                >
                    <p className="font-poppins font-medium text-sm">{t('modals.uploadFileMethod.dragAndDrop')}</p>
                    <p className="font-poppins text-xs font-normal text-[#90A4AE]">{t('modals.uploadFileMethod.or')}</p>
                    <Button
                        className="cursor-pointer rounded py-1.5 h-[34px]"
                        onClick={openFileSelector}
                        disabled={false}
                    >
                        {t('modals.uploadFileMethod.browse')}
                    </Button>
                    <input
                        ref={fileInputRef}
                        type="file"
                        className="hidden"
                        accept=".xls, .xlsx"
                        onChange={handleFileSelection}
                    />
                    <p className="font-roboto text-xs font-normal text-[#90A4AE]">
                        {t('settings.connections.availableTypes')}
                    </p>
                </div>
                {fileSelected !== null && (
                    <div className="flex bg-[#37474F] p-3 rounded-[4px] h-[60px] items-center justify-between mt-4">
                        <div className="flex justify-start items-center">
                            <FileX className="h-[18px] w-[18px] text-[#D5C2FB] mr-3" />
                            <div className="flex flex-col gap-1">
                                <p className="font-roboto font-medium text-[14px] leading-[14px]">
                                    {fileSelected.name}
                                </p>
                                <p className="font-roboto font-normal text-[12px] leading-[12px]">
                                    {getFileSize(fileSelected)}
                                </p>
                            </div>
                        </div>
                        <div className="flex justify-end items-center gap-2">
                            <Progress value={progress} className="w-24 h-2" />
                            {result === 'success' && <RiCheckboxCircleFill className="h-4 text-[#059669]" />}
                            {result !== 'success' && result !== 'error' && (
                                <X
                                    className="h-4 cursor-pointer"
                                    onClick={() => {
                                        setFile(null);
                                        if (fileInputRef.current) {
                                            fileInputRef.current.value = '';
                                        }
                                    }}
                                />
                            )}
                            {result === 'error' && <RiCloseCircleFill className="h-4 text-[#FCA5A5]" />}
                        </div>
                    </div>
                )}
            </YpfIntegrationModalSection>
            <YpfIntegrationModalSection className="flex-row justify-end">
                <Button onClick={handleClose} className="mr-2" variant="ghost">
                    {t('settings.connections.cancel')}
                </Button>
                <Button onClick={handleUpload} disabled={fileSelected === null || isPending || result === 'success'}>
                    {t('settings.connections.upload')}
                </Button>
            </YpfIntegrationModalSection>
        </YpfIntegrationModalBase>
    );
};

export default YpfIntegrationUpload;
